import { useState } from "react";
import { Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface GeoReliabilityActionsProps {
  promptId: string;
  worksCount: number;
  issueCount: number;
  onUpdate?: (worksCount: number, issueCount: number) => void;
}

export function GeoReliabilityActions({
  promptId,
  worksCount: initialWorksCount,
  issueCount: initialIssueCount,
  onUpdate,
}: GeoReliabilityActionsProps) {
  const { user } = useAuth();
  const [worksCount, setWorksCount] = useState(initialWorksCount);
  const [issueCount, setIssueCount] = useState(initialIssueCount);
  const [isVoting, setIsVoting] = useState(false);
  const [userVote, setUserVote] = useState<"works" | "issue" | null>(null);

  const handleVote = async (type: "works" | "issue", e: React.MouseEvent) => {
    e.stopPropagation();

    if (!user) {
      toast.error("Sign in to vote on prompt reliability");
      return;
    }

    if (userVote === type) {
      toast.info("You've already voted");
      return;
    }

    setIsVoting(true);
    try {
      // Get current geo_metadata
      const { data: promptData, error: fetchError } = await supabase
        .from("prompts")
        .select("geo_metadata")
        .eq("id", promptId)
        .single();

      if (fetchError) throw fetchError;

      const currentMetadata = promptData?.geo_metadata as any || {};
      const reliability = currentMetadata.reliability || {
        tested_by_author: false,
        community_verified: 0,
        reported_issues: 0,
      };

      // Update counts
      const newReliability = {
        ...reliability,
        community_verified: type === "works" 
          ? reliability.community_verified + 1 
          : reliability.community_verified,
        reported_issues: type === "issue"
          ? reliability.reported_issues + 1
          : reliability.reported_issues,
      };

      const { error: updateError } = await supabase
        .from("prompts")
        .update({
          geo_metadata: {
            ...currentMetadata,
            reliability: newReliability,
          },
        })
        .eq("id", promptId);

      if (updateError) throw updateError;

      // Update local state
      if (type === "works") {
        setWorksCount((prev) => prev + 1);
      } else {
        setIssueCount((prev) => prev + 1);
      }
      setUserVote(type);

      toast.success(type === "works" ? "Thanks for confirming!" : "Issue reported");
      onUpdate?.(
        type === "works" ? worksCount + 1 : worksCount,
        type === "issue" ? issueCount + 1 : issueCount
      );
    } catch (error) {
      console.error("Vote error:", error);
      toast.error("Failed to record vote");
    } finally {
      setIsVoting(false);
    }
  };

  const WorksButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={(e) => handleVote("works", e)}
      disabled={isVoting || userVote === "works"}
      className={cn(
        "h-7 px-2 text-xs gap-1",
        userVote === "works"
          ? "text-green-400 bg-green-400/10"
          : "text-white/40 hover:text-green-400 hover:bg-green-400/10"
      )}
    >
      <Check className="h-3 w-3" />
      <span>Works</span>
      <span className="text-white/30">{worksCount}</span>
    </Button>
  );

  const IssueButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={(e) => handleVote("issue", e)}
      disabled={isVoting || userVote === "issue"}
      className={cn(
        "h-7 px-2 text-xs gap-1",
        userVote === "issue"
          ? "text-amber-400 bg-amber-400/10"
          : "text-white/40 hover:text-amber-400 hover:bg-amber-400/10"
      )}
    >
      <AlertTriangle className="h-3 w-3" />
      <span>Issue</span>
      <span className="text-white/30">{issueCount}</span>
    </Button>
  );

  if (!user) {
    return (
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>{WorksButton}</TooltipTrigger>
          <TooltipContent>Sign in to vote</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>{IssueButton}</TooltipTrigger>
          <TooltipContent>Sign in to vote</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {WorksButton}
      {IssueButton}
    </div>
  );
}
